import { useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { AppRole } from "@shop-ledger/shared-types";
import { MobileDrawerNav, type NavItem } from "@shop-ledger/ui-components";
import { usePlatformAuth } from "../auth/PlatformAuthContext";
import { usePlatformClient } from "../api/ClientContext";

interface NavEntry {
  key: string;
  label: string;
  href: string;
}

const NAV_ENTRIES: NavEntry[] = [
  { key: "overview", label: "Overview", href: "/" },
  { key: "tenants", label: "Businesses", href: "/tenants" },
  { key: "onboarding", label: "Onboard Business", href: "/onboarding" },
  { key: "health", label: "System Health", href: "/health" },
  { key: "logs", label: "Logs", href: "/logs" },
  { key: "queues", label: "Operational Queues", href: "/queues" },
  { key: "marketplace", label: "Marketplace Orders", href: "/marketplace/orders" },
  { key: "audit-log", label: "Audit Log", href: "/audit-log" },
  { key: "settings", label: "General Settings", href: "/settings" },
];

/**
 * `MobileDrawerNav` (`@shop-ledger/ui-components`) structurally requires an `AppRole` —
 * the *tenant-realm* role type (§10: OWNER/BRANCH_MANAGER/ACCOUNTANT/TELLER/ECOM_MGR),
 * because it reuses `NavShell.tsx`'s `visibleFor` role filter rather than redefining it.
 * A `PlatformOperator` isn't one of those roles and never should be coerced into one
 * (see this file's own doc comment below on why `NavShell` itself isn't used here) — but
 * `visibleFor`'s actual logic (`NavShell.tsx`) only ever consults an item's own `roles`
 * array (`!item.roles || item.roles.length === 0 || item.roles.includes(role)`), and
 * `MOBILE_NAV_ITEMS` below never sets `roles` on any item (every nav entry is visible to
 * every operator, per this file's existing "no §10-style per-role menu in this app" doc
 * comment). So this placeholder value is never actually consulted for filtering — it
 * exists only to satisfy the prop's type, not because platform-admin has adopted the
 * tenant-realm role model.
 */
const UNUSED_PLACEHOLDER_ROLE: AppRole = "OWNER";

/** How often the nav polls `GET /platform/queues/alert-summary` (§8's "alerting
 * thresholds") for its "Operational Queues" badge — frequent enough that a forming
 * backlog shows up promptly, not so frequent it's a meaningful load on its own. */
const ALERT_POLL_INTERVAL_MS = 60_000;

/**
 * A hand-rolled nav shell for this app, deliberately NOT `@shop-ledger/ui-components`'
 * `NavShell` — that component's `role: AppRole` prop is typed to the five *tenant-realm*
 * roles (§10: OWNER/BRANCH_MANAGER/ACCOUNTANT/TELLER/ECOM_MGR). A `PlatformOperator` is
 * not one of those roles and never should be coerced into pretending to be one just to
 * reuse a component — §9.1's whole point is that this is a genuinely separate realm. This
 * component reuses `ui-components/styles.css`'s `.sl-nav-shell` CSS classes directly for
 * visual consistency with the rest of the design system, without borrowing the
 * role-filtering *behavior*, which doesn't apply here (every nav item is visible to every
 * authenticated operator — there's no §10-style per-role menu in this app).
 */
export function PlatformNav({ children }: { children: ReactNode }) {
  const { operator, logout } = usePlatformAuth();
  const client = usePlatformClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [staleQueueCount, setStaleQueueCount] = useState(0);

  useEffect(() => {
    if (!operator) return;

    let cancelled = false;
    async function poll() {
      try {
        const summary = await client.getQueueAlertSummary();
        if (!cancelled) {
          setStaleQueueCount(summary.sync_conflicts_stale_count + summary.payment_pending_stale_count);
        }
      } catch {
        // A transient failure here shouldn't be presented as "no backlog" — leave the
        // last-known count in place rather than clearing it to 0.
      }
    }
    void poll();
    const interval = setInterval(() => void poll(), ALERT_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [operator, client]);

  if (!operator) return <>{children}</>;

  // "/" (Overview) needs an exact match — every other route also "starts with" "/", which
  // would otherwise leave Overview permanently highlighted alongside whatever page is
  // actually active.
  function isEntryActive(entry: NavEntry): boolean {
    return entry.href === "/" ? location.pathname === "/" : location.pathname.startsWith(entry.href);
  }

  function queueBadge(count: number) {
    if (count <= 0) return null;
    return (
      <span
        className="sl-nav-shell__badge"
        style={{
          marginLeft: 8,
          background: "#dc2626",
          color: "#fff",
          borderRadius: 999,
          padding: "0 7px",
          fontSize: 12,
          lineHeight: "18px",
        }}
        title={`${count} queue item(s) past the alert threshold`}
      >
        {count}
      </span>
    );
  }

  const activeEntry = NAV_ENTRIES.find(isEntryActive);

  // Same items feed both the desktop sidebar (rendered directly below) and the mobile
  // drawer — one array, so a nav entry (or the queue-alert badge) added here never drifts
  // between the two surfaces. No `roles` set on any item (see `UNUSED_PLACEHOLDER_ROLE`'s
  // doc comment above): every entry is visible to every operator. The "Operational Queues"
  // stale-count badge rides along as `icon` (rendered before the label inside the drawer,
  // rather than after as on the desktop link) so it's carried into the drawer too, not
  // dropped — a harmless positional difference, not a lost signal.
  const mobileNavItems: NavItem[] = NAV_ENTRIES.map((entry) => ({
    key: entry.key,
    label: entry.label,
    href: entry.href,
    icon: entry.key === "queues" ? queueBadge(staleQueueCount) : undefined,
  }));

  const headerContent = (
    <>
      <strong>ZEL-MART — Platform Admin</strong>
      <div className="muted" style={{ color: "#cbd5e1" }}>
        {operator.email}
      </div>
    </>
  );

  const footerContent = (
    <>
      <button type="button" className="sl-button sl-button--ghost sl-button--md" onClick={() => navigate("/account")}>
        My account
      </button>
      <button type="button" className="sl-button sl-button--ghost sl-button--md" onClick={() => void logout()}>
        Sign out
      </button>
    </>
  );

  return (
    <>
      {/* Phone-width takeover (<=768px, see `.sl-mobile-topbar` in index.css, matching
          dashboard-admin's own equivalent breakpoint): the sidebar below is hidden and this
          hamburger top bar takes its place — only the nav chrome swaps, `.sl-nav-shell__content`
          (the page body) is untouched either way. Rendered unconditionally so its CSS
          `display: none` default never causes a layout flash while it decides. */}
      <div className="sl-mobile-topbar">
        <MobileDrawerNav
          role={UNUSED_PLACEHOLDER_ROLE}
          items={mobileNavItems}
          activeKey={activeEntry?.key}
          onNavigate={(item) => navigate(item.href)}
          header={headerContent}
          footer={footerContent}
        />
        <strong className="sl-mobile-topbar__brand">ZEL-MART — Platform Admin</strong>
      </div>

      <div className="sl-nav-shell">
        <nav className="sl-nav-shell__sidebar" aria-label="Primary">
          <div className="sl-nav-shell__header">{headerContent}</div>
          <ul className="sl-nav-shell__list">
            {NAV_ENTRIES.map((entry) => {
              const isActive = isEntryActive(entry);
              return (
                <li key={entry.key}>
                  <a
                    href={entry.href}
                    className={["sl-nav-shell__link", isActive ? "sl-nav-shell__link--active" : null]
                      .filter(Boolean)
                      .join(" ")}
                    aria-current={isActive ? "page" : undefined}
                    onClick={(event) => {
                      event.preventDefault();
                      navigate(entry.href);
                    }}
                  >
                    <span>{entry.label}</span>
                    {entry.key === "queues" ? queueBadge(staleQueueCount) : null}
                  </a>
                </li>
              );
            })}
          </ul>
          <div className="sl-nav-shell__footer">{footerContent}</div>
        </nav>
        <main className="sl-nav-shell__content">{children}</main>
      </div>
    </>
  );
}
